import React, { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import { Switch, Route } from 'react-router-dom'
import { push } from 'connected-react-router'
import { connect } from 'react-redux'
import BEMHelper from '@ynap/bem-helper'
import { addressActions, getAddresses } from 'app/state/address-state'
import { cartActions } from '@ynap/cart-state'
import { countryActions } from '@ynap/country-state'
import { contentActions } from '@ynap/content-state'
import { userActions, getUserDetails, getPrivacySettings } from '@ynap/user-state'
import { ScrollToTopOnMount as ScrollToTop } from '@ynap/scroll-to-top'
import { orderActions, getOrderHistory, getOrderDetails } from 'app/state/order-history-state'
import { creditsActions, getStoreCredit } from 'app/state/credits-state'
import { reservationsActions, getReservations } from 'app/state/reservations-state'
import { walletActions } from 'app/state/wallet-state'
import getLinks from 'app/state/myaccount-state/selectors/getLinks'
import getUserSummary from 'app/state/myaccount-state/actions/getUserSummary'
import { MetaTags } from '@ynap/meta-tags'

import {
    AccountDetails,
    AddressBook,
    Credits,
    Rewards,
    MyAccountNavWrapper,
    MyAccountLanding,
    OrderHistory,
    OrderDetails,
    CardWallet,
    PrivacySettings,
    ReferAFriend,
    Reservations,
    getConfig,
} from 'app/components'

import { getAccount, getLanding, getRewardsLanding } from 'app/state/myaccount-state/selectors'
import { getMetaData } from './App/utils'
import ServiceMessage from 'app/components/ServiceMessage/ServiceMessage'
import getServiceMessage from 'app/state/myaccount-state/selectors/getServiceMessage'

const classNames = new BEMHelper('Account')

const CreditsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getStoreCredit(state),
    }),
    { fetchStoreCredit: creditsActions.fetchStoreCredit }
)(Credits)

const LandingContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getLanding(state, props),
        dir: state.appState?.locale?.dir,
    }),
    { ...countryActions.fetchCountry, fetchContent: contentActions.fetchContent }
)(MyAccountLanding)

function mapStateToAddressBookProps(state, ownProps) {
    return {
        ...getConfig(state, ownProps),
        ...getAddresses(state, ownProps),
    }
}

const AddressBookContainer = connect(mapStateToAddressBookProps, {
    ...addressActions,
    ...countryActions,
    push,
})(AddressBook)

const OrderHistoryContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getOrderHistory(state, props),
    }),
    orderActions
)(OrderHistory)

const OrderDetailsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getOrderDetails(state, props),
    }),
    { ...orderActions, push, ...countryActions, fetchContent: contentActions.fetchContent, ...countryActions }
)(OrderDetails)

const AccountDetailsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getUserDetails(state, props),
    }),
    { ...userActions, push }
)(AccountDetails)

const CardWalletContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        wallet: state.wallet,
        addCardState: state.addCard,
    }),
    { ...walletActions, push }
)(CardWallet)

const ReservationsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getReservations(state, props),
        cartState: state.cart
    }),
    { ...reservationsActions, addToCart: cartActions.addToCart, addMultipleToCart: cartActions.addMultipleToCart }
)(Reservations)

const RewardsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getRewardsLanding(state, props),
        dir: state.appState?.locale?.dir,
    }),
    {
        ...countryActions.fetchCountry,
        fetchContent: contentActions.fetchContent,
    }
)(Rewards)

const PrivacySettingsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getPrivacySettings(state, props),
    }),
    { ...userActions, ...contentActions, ...countryActions }
)(PrivacySettings)

const ReferAFriendContainer = connect((state, props) => {
    return {
        country: getConfig(state, props).locale.country.toLowerCase(),
        language: getConfig(state, props).locale.language.toLowerCase(),
    }
})(ReferAFriend)

const ServiceMessageContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getServiceMessage(state, props),
    }),
    {
        fetchContent: contentActions.fetchContent,
    }
)(ServiceMessage)

class Account extends PureComponent {
    static propTypes = {
        messages: PropTypes.shape({
            menu: PropTypes.func.isRequired,
        }).isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }),
        locale: PropTypes.shape({
            id: PropTypes.string,
        }),
        links: PropTypes.arrayOf(PropTypes.object),
        pages: PropTypes.object,
    }
    state = {
        userSummary: undefined,
    }

    componentDidMount() {
        const { locale, fetchCountry } = this.props

        fetchCountry()

        getUserSummary(locale).then((userSummary) => {
            this.setState({ userSummary })
        })
    }

    render() {
        const { messages, location, locale, pages, config, nativeApp, ...rest } = this.props
        const { userSummary } = this.state
        const { isEip } = { ...userSummary }
        const {
            sizeGridFeatureToggle,
            showExchangeDerivedOrderIds,
            fullPageHeader,
            enableGiftCards,
            enableStoreCredit,
            storeCreditExclusionList,
            enableServiceMessageCoremediaContent,
        } = config.head.flags
        const { emptyLink, links, business, buyapowaCampaign, buyapowaURL, buyapowaMarket } = config.head
        const {
            addressBook,
            myorders,
            orderdetails,
            accountdetails,
            savedcards,
            credits,
            rewardsProgram,
            reservations,
            privacy,
            referAFriend,
        } = pages
        const accountNavProps = {
            messages,
            location,
            getLinks,
            nativeApp,
            fullPageHeader,
            enableStoreCredit,
            storeCreditExclusionList,
            locale,
            userSummary,
        }

        const metaData = getMetaData(messages, location, business)

        const renderServiceMessage = () => {
            return (
                <ServiceMessageContainer
                    enableEditorialContent={enableServiceMessageCoremediaContent}
                    isEip={isEip}
                    messages={messages}
                    links={links}
                    locale={locale}
                />
            )
        }

        return (
            <>
                <MetaTags metaData={metaData} />
                <Switch location={location}>
                    <Route
                        path="/:locale/account"
                        exact
                        render={() => (
                            <ScrollToTop>
                                <LandingContainer
                                    className={classNames('landingPage')}
                                    messages={messages}
                                    enableStoreCredit={enableStoreCredit}
                                    storeCreditExclusionList={storeCreditExclusionList}
                                    userSummary={userSummary}
                                    locale={locale}
                                    {...rest}
                                />
                            </ScrollToTop>
                        )}
                    />
                    <Route
                        render={() => (
                            <Fragment>
                                {addressBook ? (
                                    <Route
                                        path={`/:locale${addressBook.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.addressBook?.title?.()}
                                                >
                                                    <AddressBookContainer
                                                        messages={messages}
                                                        locale={locale}
                                                        {...rest}
                                                        config={config}
                                                        nativeApp={nativeApp}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {reservations ? (
                                    <Route
                                        path={`/:locale${reservations.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.reservations?.title?.()}
                                                >
                                                    <ReservationsContainer
                                                        messages={messages}
                                                        userSummary={userSummary}
                                                        noCardsLink={`/${locale.id}${emptyLink}`}
                                                        {...rest}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {myorders ? (
                                    <Route
                                        path={`/:locale${myorders.to}`}
                                        exact
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.myorders?.title?.()}
                                                >
                                                    <OrderHistoryContainer
                                                        {...rest}
                                                        messages={messages}
                                                        pageSize={6}
                                                        noOrdersLink={`/${locale.id}${emptyLink}`}
                                                        showExchangeDerivedOrderIds={showExchangeDerivedOrderIds}
                                                        nativeApp={nativeApp}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {orderdetails ? (
                                    <Route
                                        path={`/:locale${orderdetails.to}`}
                                        render={({ match: { params }, location }) => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    params={params}
                                                    {...accountNavProps}
                                                    title={messages?.pages?.orderdetails?.title?.()}
                                                    preTitle={messages?.pages?.myorders?.title?.()}
                                                >
                                                    <OrderDetailsContainer
                                                        messages={messages}
                                                        location={location}
                                                        externalOrderId={params.externalOrderId}
                                                        sizeGridFeatureToggle={sizeGridFeatureToggle}
                                                        showExchangeDerivedOrderIds={showExchangeDerivedOrderIds}
                                                        links={links}
                                                        nativeApp={nativeApp}
                                                        {...rest}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {accountdetails ? (
                                    <Route
                                        path={`/:locale${accountdetails.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.accountdetails?.title?.()}
                                                >
                                                    <AccountDetailsContainer
                                                        messages={messages}
                                                        locale={locale}
                                                        {...rest}
                                                        location={location}
                                                        isImpersonatedBy={userSummary?.isImpersonatedBy}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {savedcards ? (
                                    <Route
                                        path={`/:locale${savedcards.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.savedcards?.title?.()}
                                                >
                                                    <CardWalletContainer
                                                        messages={messages}
                                                        nativeApp={nativeApp}
                                                        {...rest}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {credits ? (
                                    <Route
                                        path={`/:locale${credits.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.credits?.title?.()}
                                                >
                                                    <CreditsContainer
                                                        messages={messages}
                                                        enableGiftCards={enableGiftCards}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {rewardsProgram && (
                                    <Route
                                        path={`/:locale${rewardsProgram.optedInPath}`}
                                        render={() => (
                                            <RewardsContainer
                                                locale={locale}
                                                messages={messages}
                                                userSummary={userSummary}
                                                {...rest}
                                            />
                                        )}
                                    />
                                )}
                                {privacy ? (
                                    <Route
                                        path={`/:locale${privacy.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.privacy?.title?.()}
                                                >
                                                    <PrivacySettingsContainer messages={messages} {...rest} />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {referAFriend ? (
                                    <Route
                                        path={`/:locale${referAFriend.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <MyAccountNavWrapper
                                                    {...accountNavProps}
                                                    title={messages?.pages?.referAFriend?.title?.()}
                                                >
                                                    <ReferAFriendContainer
                                                        messages={messages}
                                                        buyapowaURL={buyapowaURL}
                                                        buyapowaMarket={buyapowaMarket}
                                                        buyapowaCampaign={buyapowaCampaign}
                                                        rootElem="bp_div"
                                                        errorElem="bp_error"
                                                        {...rest}
                                                    />
                                                </MyAccountNavWrapper>
                                                {!nativeApp && renderServiceMessage()}
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                            </Fragment>
                        )}
                    />
                </Switch>
            </>
        )
    }
}

export default connect(getAccount, { ...creditsActions, ...countryActions })(Account)
